import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  isApiErrorWithStatus,
  login as loginRequest,
  logout as logoutRequest,
  refreshSession as refreshSessionRequest,
  resendVerification as resendVerificationRequest,
  showApiError,
  verifyEmail as verifyEmailRequest,
} from '~/services';
import modalStatusService from '~/services/modalStatus';
import { clearUser, userSetData } from '~/store/user/slices';
import { mapApiUserToState } from '~/store/user/mappers';
import { getUser } from '~/store/user/thunks';
import { clearEntitlements } from '~/store/entitlements/slices';
import { fetchEntitlements } from '~/store/entitlements/thunks';
import {
  getRefreshToken,
  removeTokens,
  saveAccessToken,
  saveRefreshToken,
} from '~/utils/storage';

import {
  loginFailure,
  loginRequest as loginStart,
  loginSuccess,
  logout as logoutAction,
} from './slices';
import type { propsRequestLogin } from './interfaces';

export const loginUser = createAsyncThunk(
  'login/auth',
  async (
    { username, password }: propsRequestLogin,
    { dispatch },
  ): Promise<boolean> => {
    dispatch(loginStart());
    try {
      const { status, data } = await loginRequest({
        email: username,
        password,
      });

      if (status === 200 && data?.tokens?.accessToken) {
        saveAccessToken(data.tokens.accessToken);
        saveRefreshToken(data.tokens.refreshToken);
        if (data.user) {
          dispatch(userSetData(mapApiUserToState(data.user)));
        }
        dispatch(loginSuccess());
        await dispatch(getUser());
        void dispatch(fetchEntitlements());
        return true;
      }

      dispatch(loginFailure());
      if (status === 401) {
        showInvalidCredentialsModal();
        return false;
      }
      return showApiError(new Error('Unexpected login response'));
    } catch (error) {
      dispatch(loginFailure());
      if (isApiErrorWithStatus(error, 401)) {
        showInvalidCredentialsModal();
        return false;
      }
      return showApiError(error);
    }
  },
);

function showInvalidCredentialsModal() {
  modalStatusService.show({
    type: 'alert',
    icon: 'icon-close',
    iconBgColor: 'bg-warning-01',
    iconBorderColor: 'border-warning-01',
    iconColor: '#992929',
    title: 'Usuário ou senha inválidos!',
    description:
      'Parece que o usuário ou senha estão incorretos.\nTente novamente.',
    textFirstButton: 'Entendi',
    showFirstButton: true,
    onClickFirstButton: modalStatusService.hide,
    handleCloseModal: modalStatusService.hide,
  });
}

export const logoutUser = createAsyncThunk(
  'login/logout',
  async (_, { dispatch }) => {
    const refreshToken = getRefreshToken();
    removeTokens();
    dispatch(logoutAction());
    dispatch(clearUser());
    dispatch(clearEntitlements());
    if (refreshToken) {
      try {
        await logoutRequest(refreshToken);
      } catch {
      }
    }
  },
);

export const verifyEmailToken = createAsyncThunk(
  'login/verify-email',
  async (token: string, { dispatch }) => {
    try {
      const response = await verifyEmailRequest(token);
      if (response.status === 200 || response.status === 204) {
        const refreshToken = getRefreshToken();
        if (refreshToken) {
          try {
            const refreshed = await refreshSessionRequest(refreshToken);
            if (refreshed.data?.tokens?.accessToken) {
              saveAccessToken(refreshed.data.tokens.accessToken);
              saveRefreshToken(refreshed.data.tokens.refreshToken);
            }
          } catch {
          }
        }
        await dispatch(getUser());
        return true;
      }
      return false;
    } catch (error) {
      showApiError(error, {
        title: 'Não foi possível confirmar seu email',
        fallbackDescription:
          'O link de confirmação pode ter expirado. Solicite um novo email e tente novamente.',
      });
      return false;
    }
  },
);

export const resendEmailVerification = createAsyncThunk(
  'login/resend-verification',
  async () => {
    try {
      const response = await resendVerificationRequest();
      return response.status === 200 || response.status === 204;
    } catch (error) {
      showApiError(error, { title: 'Não foi possível reenviar o email' });
      return false;
    }
  },
);
